// Auth wrappers around supabase.auth.
//
// Lifted from CloudTrust360's lib/auth.js with one Govern360 addition:
// signInWithAzure() uses Supabase's built-in 'azure' provider (the user
// confirmed this in chunk-0 planning — no MSAL flow). The Azure app
// registration must list this Supabase project's callback URL as a
// redirect URI.

import { supabase, ENV } from './supabase.js';

const REDIRECT_URL = ENV.AUTH_REDIRECT_URL || (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined);

/* -------------------------------------------------------------------------- */
/* Email + password                                                           */
/* -------------------------------------------------------------------------- */
export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { user: data?.user || null, session: data?.session || null, error };
}

export async function signUpWithEmail(email, password, metadata = {}) {
  // metadata flows into raw_user_meta_data and is read by the handle_new_user()
  // trigger to populate profiles.full_name on first sign-up.
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: metadata, emailRedirectTo: REDIRECT_URL },
  });
  return { user: data?.user || null, session: data?.session || null, error };
}

export async function sendPasswordReset(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: REDIRECT_URL,
  });
  return { error };
}

/* -------------------------------------------------------------------------- */
/* OAuth — Google                                                             */
/* -------------------------------------------------------------------------- */
export async function signInWithGoogle(redirectTo = REDIRECT_URL) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      // 'select_account' forces the Google account chooser even when the
      // user is signed in to only one Google account — important for shared
      // machines and for staff who toggle between personal and work.
      queryParams: { prompt: 'select_account' },
    },
  });
  return { data, error };
}

/* -------------------------------------------------------------------------- */
/* OAuth — Azure (Microsoft Entra ID) via Supabase's built-in provider        */
/* -------------------------------------------------------------------------- */
export async function signInWithAzure(redirectTo = REDIRECT_URL) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: {
      redirectTo,
      // 'email openid profile' is the standard Entra scope set. Without
      // 'email' Supabase can't populate auth.users.email from the OAuth
      // response — leading to the classic "user signed in but profile
      // shows no email" bug.
      scopes: 'email openid profile',
    },
  });
  return { data, error };
}

/* -------------------------------------------------------------------------- */
/* Session helpers                                                            */
/* -------------------------------------------------------------------------- */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  return { session: data?.session || null, error };
}

export function onAuthStateChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  // Return an unsubscribe fn (CT360-compatible shape).
  return () => data?.subscription?.unsubscribe?.();
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/* -------------------------------------------------------------------------- */
/* Profile + tenant hydration                                                 */
/* -------------------------------------------------------------------------- */
// Loads the user's profile row + their PRIMARY tenant (the one they last used,
// or their only one). Multi-tenant membership lookups happen separately in
// useTenant() so this call stays cheap for the common single-tenant case.
export async function getProfile(userId) {
  if (!userId) return { profile: null, error: null };
  const { data, error } = await supabase
    .from('profiles')
    .select(
      `
      id,
      full_name,
      avatar_url,
      role,
      nav_favorites,
      primary_tenant_id,
      tenant:primary_tenant_id (
        id, slug, name, plan, industry, size, primary_color, logo_url, settings
      )
    `
    )
    .eq('id', userId)
    .maybeSingle();
  return { profile: data, error };
}
